import styled, { css } from 'styled-components/macro';
import { space } from 'styled-system';
import ReactCalendar from 'react-calendar';

import { colors } from '../../Style/Theme';

// Might come from shop settings at some point
const FEW_THRESHOLD = 30;
const SOME_THRESHOLD = 60;

const getCapacityColor = (capacity: number | undefined | null, disabled: boolean) => {
  if (disabled) return '#fff';

  if (capacity) {
    if (capacity < FEW_THRESHOLD) return colors.orange;
    if (capacity < SOME_THRESHOLD) return colors.yellow;
  }

  return colors.green;
};

export const CapacityIndicator = styled.div<{
  capacity: number | undefined | null;
  disabled: boolean;
}>`
  width: 12px;
  height: 4px;
  border-radius: 3px;
  background-color: ${(props) => getCapacityColor(props.capacity, props.disabled)};
  margin: 4px auto 0;
`;

export const CalendarWrapper = styled.div<{ $marginBottom: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 50%;
  max-width: 300px;
  min-width: 230px;
  height: fit-content;

  @media screen and (max-width: 767px) {
    align-self: center;
    margin-bottom: ${(props) => (props.$marginBottom ? '24px' : 0)};
    max-width: none;
  }
`;

const shadow = '0 4px 6px rgba(50, 50, 93, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';

const pseudoElementBorder = css`
  &::after {
    content: '';
    position: absolute;
    top: 10%;
    left: 15%;
    width: 70%;
    height: 80%;
    border-radius: 6px;
    border: 1px solid ${(props) => props.theme.colors.primary};
  }
`;

// Ugly styles are ugly
// TODO: Clean up calendar styles
export const StyledCalendar = styled(ReactCalendar)`
  font-family: ${(props) => props.theme.fontText};
  flex: 1 1 50%;
  /* max-width: 300px; */
  max-height: fit-content;
  background-color: #fff;
  border: none;
  border-radius: 4px;
  box-shadow: ${(props) => (props.theme.shadows ? shadow : 'none')};
  ${space}

  &.react-calendar button {
    border: 0;
  }
  &.react-calendar button:enabled {
    cursor: pointer;
  }
  .react-calendar__navigation {
    height: 44px;
    margin-bottom: 1em;
  }
  .react-calendar__navigation button {
    min-width: 44px;
    background: none;
  }
  .react-calendar__month-view__weekdays {
    text-align: center;
  }
  .react-calendar__tile {
    max-width: 100%;
    text-align: center;
    padding: 0.9em 0.5em;
    background-color: #fff;
    border-radius: 4px;
    position: relative;

    ${(props) => (props.theme.name === 'starboard' ? pseudoElementBorder : '')}
  }

  /* Navigation and month */
  .react-calendar__navigation {
    display: flex;
    background-color: ${(props) => props.theme.colors.primary};
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin: 0;

    .focus-visible {
      outline: none;
      background-color: transparent;
      border-radius: 4px;
      box-shadow: 0px 0px 0px 2px ${(props) => props.theme.highLightColor};
    }
  }

  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background-color: ${(props) => props.theme.colors.primary};
    border-radius: 4px;
  }

  /* Navigation buttons */
  .react-calendar__navigation__arrow {
    font-size: 3rem;
    line-height: 44px;
    color: #fff;

    svg {
      height: 18px;
      display: inline;
      vertical-align: baseline;
      path {
        fill: #fff;
      }
    }

    :hover {
      background-color: transparent;
      color: ${(props) => props.theme.highLightColor};
      svg {
        path {
          fill: ${(props) => props.theme.highLightColor};
        }
      }
    }

    :disabled {
      color: ${(props) => props.theme.colors.primary};
      svg {
        path {
          fill: ${(props) => props.theme.colors.primary};
        }
      }
    }
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }

  /* Month text */
  .react-calendar__navigation__label {
    font-family: 'Faro-Medium';
    font-size: 1.6rem;
    color: #fff;
    text-transform: capitalize;
  }

  .react-calendar__navigation button[disabled] {
    background-color: transparent;
  }

  /* Row of weekdays */
  .react-calendar__month-view__weekdays {
    height: 30px;
  }

  /* Weekdays text */
  .react-calendar__month-view__weekdays__weekday {
    padding: 0;

    abbr {
      font-family: ${(props) => props.theme.fontText};
      font-size: 1.4rem;
      line-height: 30px;
      font-weight: normal;
      text-decoration: none;
      text-transform: capitalize;
      color: ${(props) => props.theme.textColor};
    }
  }

  /* Dates */
  .react-calendar__month-view__days {
    button {
      abbr {
        font-family: ${(props) => props.theme.fontText};
        font-size: 1.6rem;
        color: ${(props) => props.theme.textColor};
        transition: color 0.2s;
      }
      :hover {
        background-color: transparent;
        abbr {
          color: ${(props) => props.theme.highLightColor};
        }
      }

      &.focus-visible {
        outline: none;
        background: transparent;
        border-radius: 4px;
        box-shadow: 0px 0px 0px 2px ${(props) => props.theme.textColor};
      }
    }

    /* Selected */
    button.react-calendar__tile--active {
      border-radius: 4px;
      z-index: 1;
      ${(props) =>
        props.theme.name === 'starboard'
          ? `&::after {
            background-color: ${props.theme.colors.primary};
            z-index: -1;
          }`
          : `background-color: ${props.theme.textColor} !important;`}
      abbr {
        color: #fff !important;
      }
    }

    /* Disabled */
    .react-calendar__tile:disabled {
      background-color: transparent;
      abbr {
        color: #b6bbc4;
      }

      &::after {
        opacity: 0.25;
      }
    }

    /* Dates of neighboring months */
    button.react-calendar__month-view__days__day--neighboringMonth {
      abbr {
        color: ${(props) => props.theme.subTextColor};
      }

      :hover {
        background-color: transparent;
        abbr {
          color: ${(props) => props.theme.highLightColor};
        }
      }

      &.react-calendar__tile--active {
        border-radius: 4px;
        background-color: ${(props) => props.theme.textColor};
        abbr {
          color: #fff;
        }
      }
    }
  }
`;
